import React from "react";
import { modelPriceRows } from "./model-prices.mjs";

const modelKey = (provider, model) =>
  provider || model ? `${provider || ""}:${model || ""}` : "";

export function CounterpartyModelChoice({
  label,
  value,
  prices,
  purpose,
  disabled,
  onChange,
  inherit,
  inheritLabel = "Использовать модель Агента 1",
}) {
  const seen = new Set();
  const options = modelPriceRows(prices).filter((item) => {
    const key = modelKey(item.provider, item.model);
    if (!item.counterpartySelection?.[purpose] || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  const missing =
    value &&
    !options.some((item) => modelKey(item.provider, item.model) === value);
  return (
    <label>
      {label}
      <select
        value={value}
        disabled={disabled || !options.length}
        onChange={(event) => {
          const choice = options.find(
            (item) =>
              modelKey(item.provider, item.model) === event.target.value,
          );
          onChange(choice || null);
        }}
      >
        <option value="">
          {inherit ? inheritLabel : "Выберите модель"}
        </option>
        {missing && (
          <option value={value} disabled>
            {value} · недоступна для выбора
          </option>
        )}
        {options.map((item) => (
          <option
            key={modelKey(item.provider, item.model)}
            value={modelKey(item.provider, item.model)}
          >
            {item.model} · {item.provider}
          </option>
        ))}
      </select>
      {!disabled && !options.length && (
        <small>Подходящих моделей в каталоге пока нет.</small>
      )}
      {missing && (
        <small>
          Прежний выбор остаётся в черновике. Перед публикацией выберите
          доступную модель.
        </small>
      )}
    </label>
  );
}

export function CounterpartyModelSettings({
  draft,
  recommendedPrompt,
  onChange,
  prices = [],
  loading = false,
  error,
  onRetry,
  disabled = false,
  agentLabel = "1",
  includeParallel = true,
  searchEnabled = true,
}) {
  const isClone = agentLabel === "1.1";
  const researcherKey = modelKey(draft.provider, draft.model);
  const explicitSearch = modelKey(draft.searchProvider, draft.searchModel);
  const researcher = prices.find(
    (item) => modelKey(item.provider, item.model) === researcherKey,
  );
  const search = prices.find(
    (item) =>
      modelKey(item.provider, item.model) === (explicitSearch || researcherKey),
  );
  const inheritSearch = researcher?.counterpartySelection?.webSearch === true;
  const searchOptions = modelPriceRows(prices).filter(
    (item) => item.counterpartySelection?.webSearch,
  );
  const suggestedSearch =
    searchOptions.find(
      (item) =>
        item.counterpartySelection.maxOutputTokens >= draft.maxOutputTokens,
    ) || searchOptions[0];
  const maxOutput = Math.min(
    128000,
    researcher?.counterpartySelection?.maxOutputTokens || 128000,
    searchEnabled ? search?.counterpartySelection?.maxOutputTokens || 128000 : 128000,
  );
  const reasoningOptions = draft.provider === "kimi" ? [
    ["", "Стандартный режим провайдера"],
    ["low", "Без рассуждений"],
    ["high", "С рассуждениями"],
  ] : [
    ["", "Стандартный режим провайдера"],
    ["low", "Низкий"],
    ["medium", "Средний"],
    ["high", "Высокий"],
  ];
  return (
    <>
      {!isClone && loading && <p className="muted">Загружаем список моделей…</p>}
      {!isClone && error && (
        <p className="inline-problem" role="alert">
          Не удалось обновить список моделей.{" "}
          <button
            type="button"
            className="button ghost small"
            disabled={loading || disabled}
            onClick={onRetry}
          >
            Повторить
          </button>
        </p>
      )}
      <fieldset disabled={disabled}>
        <legend>Агент {agentLabel} — {agentLabel === "4" ? "фактчекер" : searchEnabled ? "исследователь" : "аналитик"}</legend>
        <p className="muted">
          Выберите модель из общего списка готовых решений. Провайдер
          определяется по выбранной модели.
        </p>
        <div className="counterparty-fields">
          <CounterpartyModelChoice
            label={`Модель Агента ${agentLabel}`}
            purpose="researcher1"
            value={researcherKey}
            prices={prices}
            disabled={disabled || loading || Boolean(error)}
            onChange={(item) => {
              const patch = {
                provider: item?.provider || "",
                model: item?.model || "",
              };
              // Keep the working search binding when changing only the main model.
              if (searchEnabled && item && !item.counterpartySelection?.webSearch &&
                  !explicitSearch && inheritSearch) {
                patch.searchProvider = draft.provider;
                patch.searchModel = draft.model;
              }
              onChange(patch);
            }}
          />
          <label>
            Максимум выходных токенов
            <input
              type="number"
              min="1"
              max={maxOutput}
              value={draft.maxOutputTokens || ""}
              onChange={(event) =>
                onChange({
                  maxOutputTokens:
                    event.target.value === ""
                      ? null
                      : Number(event.target.value),
                })
              }
            />
            <small>
              Для выбранных моделей — до {maxOutput.toLocaleString("ru-RU")}.
            </small>
            {draft.maxOutputTokens > maxOutput && (
              <small className="inline-problem" role="alert">
                Уменьшите выход до предела выбранной модели.
              </small>
            )}
          </label>
          <label>
            Режим рассуждения
            <select
              value={draft.reasoning || ""}
              onChange={(event) => onChange({ reasoning: event.target.value })}
            >
              {draft.reasoning &&
                !reasoningOptions.some(
                  ([value]) => value === draft.reasoning,
                ) && (
                  <option value={draft.reasoning}>
                    {draft.reasoning} · сохранённый режим
                  </option>
                )}
              {reasoningOptions.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <small>
              Поддержка режима зависит от модели. Стандартный режим использует
              настройки провайдера.
            </small>
          </label>
        </div>
        {!isClone ? <label>
          Инструкция {agentLabel === "4" ? "фактчекера" : searchEnabled ? "исследователя" : "аналитика"}
          <textarea
            rows="8"
            value={draft.prompt}
            onChange={(event) => onChange({ prompt: event.target.value })}
          />
        </label> : <p className="muted">Инструкция, источники, ИНН и цель проверки совпадают с Агентом 1. Модель выполняет собственное исследование параллельно; результаты сохраняются отдельно.</p>}
        {!isClone && recommendedPrompt && (
          <button
            type="button"
            className="button ghost small"
            onClick={() => onChange({ prompt: recommendedPrompt })}
          >
            Вернуть стандартную инструкцию
          </button>
        )}
        {!isClone && !String(draft.prompt || "").trim() && (
          <p className="inline-problem" role="status">
            Заполните инструкцию агента перед публикацией.
          </p>
        )}
      </fieldset>
      {searchEnabled ? <fieldset disabled={disabled}>
        <legend>Поиск документов{agentLabel !== "1" ? ` · Агент ${agentLabel}` : ""}</legend>
        <p className="muted">
          Основной моделью Агента {agentLabel} можно выбрать DeepSeek, Kimi,
          Anthropic или OpenAI из каталога. Для поиска доступны Kimi, Anthropic
          и OpenAI с подключённым поисковым инструментом и тарифом. Если основная
          модель поддерживает поиск, отдельный выбор не нужен. Для DeepSeek
          можно выбрать поиск Kimi — OpenAI не требуется.
        </p>
        <div className="counterparty-fields">
          <CounterpartyModelChoice
            label={`Модель поиска документов${agentLabel !== "1" ? ` · Агент ${agentLabel}` : ""}`}
            purpose="webSearch"
            value={explicitSearch}
            prices={prices}
            inherit={inheritSearch}
            inheritLabel={`Использовать модель Агента ${agentLabel}`}
            disabled={disabled || loading || Boolean(error)}
            onChange={(item) =>
              onChange({
                searchProvider: item?.provider || "",
                searchModel: item?.model || "",
              })
            }
          />
        </div>
        {!explicitSearch &&
          researcherKey &&
          !inheritSearch &&
          !loading &&
          !error && (
            <p className="inline-problem">
              Для выбранной модели Агента {agentLabel} укажите отдельную модель
              с поддержкой поиска: например, Kimi.
            </p>
          )}
        {!explicitSearch && researcherKey && !inheritSearch && suggestedSearch &&
          !loading && !error && !disabled && (
            <button
              type="button"
              className="button ghost small"
              onClick={() => onChange({
                searchProvider: suggestedSearch.provider,
                searchModel: suggestedSearch.model,
              })}
            >
              Выбрать {suggestedSearch.model} для поиска
            </button>
          )}
        {explicitSearch && !inheritSearch && researcher && search && (
          <p className="muted">
            {search.model} ищет информацию в интернете, {researcher.model}
            {" "}подготавливает {agentLabel === "4" ? "файл замечаний" : "досье"}. Расходы обеих моделей входят в общий лимит запуска.
          </p>
        )}
      </fieldset> : <p className="muted">Агент 3 использует материалы исследователей. Отдельная модель веб-поиска ему не требуется.</p>}
      {agentLabel === "1" && includeParallel && <>
        <label className="counterparty-parallel-toggle">
          <input type="checkbox" disabled={disabled} checked={Boolean(draft.parallelResearcher)} onChange={(event) => onChange({parallelResearcher: event.target.checked ? {provider: "", model: "", maxOutputTokens: draft.maxOutputTokens, reasoning: draft.reasoning || "", searchProvider: "", searchModel: ""} : null})}/>
          Агент 1.1 — параллельное исследование другой моделью
        </label>
        {!draft.parallelResearcher && <p className="muted">Включите Агента 1.1, чтобы выбрать вторую модель для параллельного исследования.</p>}
        <CounterpartyModelSettings
          agentLabel="1.1"
          draft={{...(draft.parallelResearcher || {}), prompt: draft.prompt}}
          prices={prices}
          loading={loading}
          error={error}
          onRetry={onRetry}
          disabled={disabled || !draft.parallelResearcher}
          onChange={(patch) => onChange({parallelResearcher: {...draft.parallelResearcher, ...patch}})}
        />
        {draft.parallelResearcher && draft.provider === draft.parallelResearcher.provider && draft.model === draft.parallelResearcher.model && draft.model && <p role="alert" className="inline-problem">Для Агента 1.1 выберите другую модель.</p>}
        {draft.parallelResearcher && <p className="muted">Денежный лимит общий для двух исследователей. Каждый выполняет отдельные платные обращения.</p>}
      </>}
    </>
  );
}
